import { nanoid } from "nanoid";
import BlockUtils from "./BlockUtils";

const ElementGroupUtils = {
  /**
   * Builds a lookup object mapping element IDs to their properties for grouped element selection
   * @param {Object} elements - Immutable List of elements
   * @param {string} containerId - ID of the parent container (block or state)
   * @returns {Object} Lookup object mapping element IDs to their properties
   */
  buildElementLookup: (elements, containerId) => {
    const lookup = {};

    elements.forEach((element, idx) => {
      const elId = element.get("id");
      const lookupItem = ElementGroupUtils.buildElementLookupItem(element, containerId, idx);

      if (elId) {
        lookup[elId] = lookupItem;
      }
    });

    return lookup;
  },

  /**
   * Builds a lookup item object containing element properties needed for grouped selection
   * @param {Immutable.Map} element - The element to create lookup item for
   * @param {string} containerId - ID of the parent container (block or state)
   * @param {number} idx - Index of the element in its parent container
   * @returns {Object} Object containing element type, key, group ID and children
   */
  buildElementLookupItem: (element, containerId, idx) => {
    const elemType = element?.get("type");
    const elemKey = BlockUtils.getElementID(containerId, idx);
    const elPid = element.get("pid");
    const elChildren = element.get("children")?.toJS();

    return { ...element.toJS(), elemType, elemKey, pid: elPid, children: elChildren, containerId };
  },

  /**
   * Creates new IDs for a group of elements while maintaining their relationships
   * @param {Object} parentElement - The parent element of the group
   * @param {Array} allElements - Array of all elements (including parent and children)
   * @returns {Object} Object containing processed parent and children with new IDs
   */
  processGroupParent: (parentElement, allElements) => {
    const newParent = {
      ...parentElement,
      id: nanoid(),
      children: [],
    };

    const newChildren = parentElement.children
      .map((childId) => {
        const childElement = allElements.find((el) => el.id === childId);
        if (!childElement) return null;

        const newChild = {
          ...childElement,
          id: nanoid(),
          pid: newParent.id,
        };
        newParent.children.push(newChild.id);
        return newChild;
      })
      .filter(Boolean);

    return {
      parent: newParent,
      children: newChildren,
    };
  },

  /**
   * Processes an array of elements, handling any groups within them
   * @param {Array} elements - Array of elements to process
   * @returns {Array} Processed elements with new IDs for groups
   */
  processElements: (elements) => {
    const processedElements = [];

    elements.forEach((element) => {
      // if the element is a child or non parent
      if (element.pid) return;

      if (element.children?.length) {
        const { parent, children } = ElementGroupUtils.processGroupParent(element, elements);
        processedElements.push(parent, ...children);
      } else {
        processedElements.push(element);
      }
    });

    return processedElements;
  },

  /**
   * Checks if an element is a child element in a group
   * @param {Object|Immutable.Map} element - The element to check
   * @param {string} [element.pid] - The group ID the element belongs to
   * @returns {boolean} True if the element is a child element, false otherwise
   */
  isGroupChild: (element) => {
    const elem = element.toJS ? element.toJS() : element;
    return Boolean(elem.pid);
  },

  /**
   * Checks if an element is a parent element that contains child elements
   * @param {Object|Immutable.Map} element - The element to check
   * @param {Array} [element.children] - Array of child element IDs
   * @returns {boolean} True if the element is a parent element, false otherwise
   */
  isGroupParent: (element) => {
    const elem = element.toJS ? element.toJS() : element;
    return Boolean(elem.children?.length);
  },
};

export default ElementGroupUtils;
